import { Button, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useState } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ApproversAssignmentMode } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import ApprovalConfigurationContext from "./ApprovalConfigurationContext";

interface SettingsState {
  approversAssignmentMode: ApproversAssignmentMode;
  currencyCode: string;
}

const GeneralApprovalSettingsSection = () => {
  const { dictionaries } = useClientContext();

  const { hasEditPermissions, approvalConfiguration, saveConfiguration } =
    useDefinedContext(ApprovalConfigurationContext);

  const [settings, setSettings] = useState<SettingsState>({
    approversAssignmentMode: approvalConfiguration.approversAssignmentMode,
    currencyCode: approvalConfiguration.conversionTargetCurrencyCode ?? "",
  });

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await saveConfiguration(
      {
        conversionTargetCurrencyCode: settings.currencyCode,
        approversAssignmentMode: settings.approversAssignmentMode,
        steps: approvalConfiguration.steps,
      },
      "Approval currency updated successfully"
    );
    setSaving(false);
  };

  const handleReset = () => {
    setSettings({
      approversAssignmentMode: approvalConfiguration.approversAssignmentMode,
      currencyCode: approvalConfiguration.conversionTargetCurrencyCode ?? "",
    });
  };

  const showCurrencySelect = approvalConfiguration.approvalRuleFieldSources.some((source) =>
    source.fields.some((field) => field.type === "Money")
  );

  const isDirty =
    settings.currencyCode !== (approvalConfiguration.conversionTargetCurrencyCode ?? "") ||
    settings.approversAssignmentMode !== approvalConfiguration.approversAssignmentMode;

  return (
    <Stack spacing={1}>
      <Typography color="textSecondary">Approvers assignment mode</Typography>
      <Select
        fullWidth
        value={settings.approversAssignmentMode}
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            approversAssignmentMode: e.target.value as SettingsState["approversAssignmentMode"],
          }))
        }
        disabled={!hasEditPermissions}
        renderValue={(value) => <Typography>{value}</Typography>}
      >
        <MenuItem value="Automatic" disabled={!approvalConfiguration.supportedAssignmentModes.includes("Automatic")}>
          <Stack spacing={0.5}>
            <Typography>Automatic</Typography>
            <Typography variant="caption" color="textSecondary">
              Approvers are assigned automatically from eligible users based on approval rules.
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem value="Manual" disabled={!approvalConfiguration.supportedAssignmentModes.includes("Manual")}>
          <Stack spacing={0.5}>
            <Typography>Manual</Typography>
            <Typography variant="caption" color="textSecondary">
              Submitter selects approvers from eligible users based on approval rules.
            </Typography>
          </Stack>
        </MenuItem>
      </Select>
      {showCurrencySelect && (
        <>
          <Typography color="textSecondary" sx={{ pt: 1 }}>
            Currency for approval rules evaluation
          </Typography>
          <Select
            sx={{ width: "14rem" }}
            value={settings.currencyCode}
            onChange={(e) => setSettings((prev) => ({ ...prev, currencyCode: e.target.value }))}
            disabled={!hasEditPermissions || dictionaries.currencies.length === 0}
          >
            {dictionaries.currencies.map(({ code }) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
      {hasEditPermissions && (
        <Stack spacing={1} direction="row" sx={{ alignItems: "center", pt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} loading={saving} disabled={!isDirty}>
            Save
          </Button>
          <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default GeneralApprovalSettingsSection;
